// Última asistencia y faltas seguidas en la CABECERA de la ficha (#1418, Mabel 15/16-oct-2026:
// «ver cuándo fue la última asistencia y cuántas faltas lleva acumuladas, para detectar bajas»).
// La definición de falta vive en el backend (lib/letsmoveFaltas.js); aquí solo se pinta.
// Si la API aún no tiene la ruta (404) el bloque no sale: el front puede publicarse antes del reinicio.
import { useEffect, useState } from 'react';
import { CalendarCheck, CalendarX } from 'lucide-react';
import { api } from '../../../services/apiClient';
import { readCache, writeCache } from '../../../services/localCache';

export interface ResumenAsistencia {
  ultima_asistencia: string | null;
  ultima_clase: string | null;
  dias_sin_venir: number | null;
  faltas_seguidas: number;
  faltas_mes: number;
  asistencias_mes: number;
  ultima_falta: string | null;
  clases_vigentes: number;
}

export function getAsistenciaResumen(id: number) {
  return api.get<{ data: ResumenAsistencia }>(`/members/${id}/asistencia-resumen`);
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
/** «12 oct» (y el año solo si no es el actual). */
export function fechaCorta(ymd: string) {
  const [y, m, d] = ymd.split('-').map(Number);
  const anio = y !== new Date().getFullYear() ? ` ${y}` : '';
  return `${d} ${MESES[m - 1]}${anio}`;
}
export function haceDias(n: number) {
  if (n <= 0) return 'hoy';
  if (n === 1) return 'ayer';
  return `hace ${n} días`;
}

/** Semáforo de faltas seguidas: ≥5 rojo, ≥3 ámbar (lo pidió recepción para detectar bajas). */
export function tonoFaltas(n: number) {
  if (n >= 5) return 'bg-red-500/10 text-red-600 dark:text-red-400';
  if (n >= 3) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400';
  return 'bg-surface-2 text-content-2';
}

const AYUDA = 'Falta = clase a la que está inscrita, con lista tomada ese día, en la que no aparece como asistente '
  + '(marcada «Faltó» o sin marca). Las clases sin lista tomada no cuentan. Las de hoy no cuentan como falta hasta mañana.';

export default function AsistenciaResumen({ memberId, onVer }: { memberId: number; onVer?: () => void }) {
  const key = `member-asist:${memberId}`;
  const [r, setR] = useState<ResumenAsistencia | null | undefined>(() => readCache<ResumenAsistencia>(key));

  useEffect(() => {
    let vivo = true;
    setR(readCache<ResumenAsistencia>(key));
    getAsistenciaResumen(memberId)
      .then((x) => { if (!vivo) return; setR(x.data); writeCache(key, x.data); })
      .catch((e) => { if (vivo && e?.status === 404) setR(null); else if (vivo) setR((prev) => prev ?? null); });
    return () => { vivo = false; };
  }, [memberId, key]);

  if (!r) return null;
  const nunca = !r.ultima_asistencia;
  const sinDatos = nunca && r.faltas_seguidas === 0 && r.faltas_mes === 0;
  const chip = 'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition hover:brightness-95';
  const lejos = r.dias_sin_venir ?? 0;
  // La última asistencia también se colorea por antigüedad: a una baja que ya salió de las listas
  // (Anna Luisa: K-Pop la dio de baja y dejó de tener sesiones) no le suben las faltas, pero sí los días.
  const tonoUltima = nunca || lejos >= 30 ? 'bg-red-500/10 text-red-600 dark:text-red-400'
    : lejos >= 14 ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';

  return (
    <div className="mt-1.5 flex flex-wrap items-center justify-center gap-2 sm:justify-start" data-testid="asistencia-resumen">
      <button type="button" onClick={onVer} data-testid="ultima-asistencia"
        title={r.ultima_clase ? `Última clase a la que vino: ${r.ultima_clase}` : AYUDA}
        className={`${chip} ${sinDatos ? 'bg-surface-2 text-content-2' : tonoUltima}`}>
        <CalendarCheck size={13} />
        {nunca
          ? (sinDatos ? 'Sin listas tomadas todavía' : 'Nunca ha asistido (desde la migración)')
          : <>Última asistencia: {fechaCorta(r.ultima_asistencia!)} ({haceDias(r.dias_sin_venir ?? 0)})</>}
      </button>
      {!sinDatos && (
        <button type="button" onClick={onVer} data-testid="faltas-seguidas"
          title={`${AYUDA}\nFaltas seguidas = desde su última asistencia.${r.ultima_falta ? ` Última falta: ${fechaCorta(r.ultima_falta)}.` : ''}`}
          className={`${chip} ${tonoFaltas(r.faltas_seguidas)}`}>
          <CalendarX size={13} /> Faltas seguidas: {r.faltas_seguidas}
        </button>
      )}
      {!sinDatos && (
        <span className="text-xs text-content-3" data-testid="faltas-mes">
          Este mes: {r.faltas_mes} {r.faltas_mes === 1 ? 'falta' : 'faltas'} · {r.asistencias_mes} {r.asistencias_mes === 1 ? 'asistencia' : 'asistencias'}
        </span>
      )}
      {r.clases_vigentes === 0 && !sinDatos && (
        <span className="text-xs text-content-3" data-testid="sin-clase">Sin clase inscrita: ya no suma faltas</span>
      )}
    </div>
  );
}
